import React, { ReactNode, useContext } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { createStructuredSelector } from 'reselect';

import { GlobalReducerState } from 'app/reducers';
import { makeSelectConfigField } from 'Containers/App/selectors';
import { makeGetSelectedPublicationPlanYear } from 'Containers/ProfilePage/selectors';
import { JVPGetAnnualSettingsResponse } from 'Utils/apiTypes';
import { FederalLimitsList, getAnnualSettingsForYear } from 'Utils/federalLimits';

/**
 * Federal (IRS) benefit limits for the user's selected plan year, resolved from the per-year
 * list on /config.federal_limits. Consumers read a single object and never see the array or
 * plan-year plumbing.
 *
 * Defaults to null so components fall back to their existing source (Contentful /
 * hardcoded) when the data — or a provider — is absent (e.g. isolated unit tests).
 */
export type FederalLimits = JVPGetAnnualSettingsResponse | null;

export const FederalLimitsContext = React.createContext<FederalLimits>(null);

interface FederalLimitsStoreProps {
  federalLimits: FederalLimitsList | null | undefined;
  selectedPlanYear: number | undefined;
}

interface FederalLimitsParentProps {
  children?: ReactNode;
}

type FederalLimitsProviderProps = FederalLimitsStoreProps & FederalLimitsParentProps;

export const FederalLimitsProvider = ({
  federalLimits,
  selectedPlanYear,
  children,
}: FederalLimitsProviderProps) => (
  <FederalLimitsContext.Provider value={getAnnualSettingsForYear(federalLimits, selectedPlanYear) ?? null}>
    {children}
  </FederalLimitsContext.Provider>
);

export const useFederalLimitsContext = () => useContext<FederalLimits>(FederalLimitsContext);

const mapStateToProps = createStructuredSelector<GlobalReducerState, FederalLimitsStoreProps>({
  federalLimits: makeSelectConfigField('federal_limits'),
  selectedPlanYear: makeGetSelectedPublicationPlanYear(),
});
const withConnect = connect(mapStateToProps);

export default compose(withConnect)(FederalLimitsProvider);
